import { Empty, Row, Select, SelectProps, Skeleton, Typography, theme } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GrispiInputProps } from '..';
import useAutocompleteUserSearch, {
    AutoCompleteUserKeyMap
} from '../../../hooks/autocomplete/useAutocompleteUserSearch';
import useAutocompleteUserSuggestions from '../../../hooks/autocomplete/useAutocompleteUserSuggestions';
import useBulkUserInfo from '../../../hooks/filtersPage/useBulkUserInfo';
import useDebounce from '../../../hooks/useDebounce';
import { getSerializedValueFromUser, getUserFriendlyValueFromUser } from '../../../utils/forms/formFieldsHelper.values';
import {
    parseMentionInfoFromUserFormValue,
    parseUserFriendlyValueFromUserValue
} from '../../../utils/forms/formHelper.parser';
import CustomDivider from '../../CustomDivider';
import { CustomFieldOptionType } from '../../CustomField/CustomField.type';
import LoadingMenu from '../../LoadingMenu';
import { allBuiltinUserValuesForActions } from '../../TriggerPage/NewTriggerForm';
import GrispiInputContainer from '../GrispiInputContainer';

type GrispiMultiUserInputProps = Omit<GrispiInputProps<'MULTI_USER'>, 'type'>;

const initialSearchTerm = '';

const parseOptionsFromValue = (value: string) => ({
    label: value.split('|').at(1) ?? value,
    value,
    disabled: parseMentionInfoFromUserFormValue(value)
});

const hydrateUserOptionsFromValue: (
    value: string,
    defaultOptions: Omit<CustomFieldOptionType, 'order'>[]
) => Omit<CustomFieldOptionType, 'order'>[] = (value, defaultOptions) => {
    if (value && typeof value === 'string') {
        const options = defaultOptions ? [...defaultOptions] : [];
        value.split(',').forEach((v) => {
            if (!allBuiltinUserValuesForActions.includes(v))
                !options.includes(parseOptionsFromValue(v)) && options.unshift(parseOptionsFromValue(v));
        });
        return options;
    }
    return defaultOptions ?? [];
};

export const transformMultiUserValue = (value: string[]) => {
    if (Array.isArray(value) && value.length > 0) return value.join(',');
    return null;
};

export const transformFormValue = (value: string) => {
    if (value && typeof value === 'string') return value.split(',');
};

export function isUserValueFormatNeeded(formValue: string) {
    if (!formValue) return false;

    return !!formValue
        .toString()
        .split(',')
        .map((v) => {
            return !allBuiltinUserValuesForActions.includes(v) && !parseUserFriendlyValueFromUserValue(v);
        })
        .filter((v) => !!v).length;
}

export const useConservativeValue = (
    formValue: string,
    setValue: GrispiInputProps<'USER'>['onChange'],
    isHookActive: boolean
): boolean => {
    const builtinValueList = formValue?.split(',')?.filter((v) => allBuiltinUserValuesForActions.includes(v));
    const idList = formValue
        ?.split(',')
        ?.filter((v) => !allBuiltinUserValuesForActions.includes(v))
        ?.join(',');
    const { data: bulkUserInfo, isLoading } = useBulkUserInfo(isHookActive ? idList : null);

    if (isHookActive && bulkUserInfo?.length > 0) {
        setValue(
            bulkUserInfo
                .map((user) => `${getSerializedValueFromUser(user)}|${getUserFriendlyValueFromUser(user)}`)
                .concat(builtinValueList)
                .join(',')
        );
    }

    useEffect(() => {
        isHookActive &&
            bulkUserInfo?.length > 0 &&
            setValue(
                bulkUserInfo
                    .map((user) => `${getSerializedValueFromUser(user)}|${getUserFriendlyValueFromUser(user)}`)
                    .concat(builtinValueList)
                    .join(',')
            );
    }, [builtinValueList, bulkUserInfo, isHookActive, setValue]);

    return isLoading;
};

const GrispiMultiUserInput = ({
    antdProps,
    conditionInput,
    containerProps,
    isNewValueAllowed,
    isAgentInput,
    onBlur,
    onChange,
    value,
    options,
    ...rest
}: GrispiMultiUserInputProps) => {
    const { t } = useTranslation('forms');
    const {
        token: { marginXXS, colorError }
    } = theme.useToken();
    const [conservativeValue, setConservativeValue] = useState<null | string>(null);
    const addButtonRef = useRef<HTMLSelectElement>();
    const [searchTerm, setSearchTerm] = useState<string>(initialSearchTerm);
    const debouncedSearchTerm = useDebounce(searchTerm, 500);
    const autocompleteKey = isAgentInput ? AutoCompleteUserKeyMap.agent : AutoCompleteUserKeyMap.user;
    const { data: searchResult, isLoading, error } = useAutocompleteUserSearch(debouncedSearchTerm, autocompleteKey);
    const { data: suggestions, setIsSuggestionRequested } = useAutocompleteUserSuggestions(
        autocompleteKey,
        !searchResult
    );
    const suggestionsWithoutSingleItem = suggestions?.filter((item) => !value?.includes(item.value));

    const isValueReadyForRender =
        value && conditionInput
            ? !isUserValueFormatNeeded(value) || (conservativeValue && !isUserValueFormatNeeded(conservativeValue))
            : true;

    useConservativeValue(value, setConservativeValue, conditionInput && !isValueReadyForRender);

    const handleOnBlur: SelectProps<string[]>['onBlur'] = () => {
        if (onBlur) onBlur();
        setSearchTerm(initialSearchTerm);
    };

    const onEnterKeyDown: SelectProps<string[]>['onInputKeyDown'] = ({ key }) => {
        if (key === 'Enter' && addButtonRef.current) addButtonRef.current.click();
    };

    const notFoundContent = isLoading ? (
        <LoadingMenu />
    ) : error ? (
        <Row justify="center" style={{ marginBottom: marginXXS }}>
            <Typography.Text style={{ color: colorError }}>{error.message}</Typography.Text>
        </Row>
    ) : debouncedSearchTerm === initialSearchTerm ? (
        <Row justify="center" style={{ marginBottom: marginXXS }}>
            <Typography.Text> {t('autocomplete.empty')}</Typography.Text>
        </Row>
    ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
    );
    const realOptions = useMemo(() => {
        return isLoading
            ? []
            : searchResult
            ? [...searchResult]
            : suggestionsWithoutSingleItem && options
            ? [
                  ...hydrateUserOptionsFromValue(conservativeValue ?? value, [
                      ...options,
                      ...suggestionsWithoutSingleItem
                  ])
              ]
            : suggestionsWithoutSingleItem
            ? [...hydrateUserOptionsFromValue(conservativeValue ?? value, [...suggestionsWithoutSingleItem])]
            : hydrateUserOptionsFromValue(conservativeValue ?? value, options);
    }, [isLoading, searchResult, suggestionsWithoutSingleItem, conservativeValue, value, options]);

    if (isValueReadyForRender) {
        return (
            <GrispiInputContainer {...containerProps}>
                <Select<string[]>
                    allowClear
                    showSearch
                    tokenSeparators={[',']} // only applies when mode='tags'
                    {...antdProps}
                    loading={isLoading}
                    notFoundContent={notFoundContent}
                    onBlur={handleOnBlur}
                    onChange={(v) => {
                        onChange(transformMultiUserValue(v));
                        conservativeValue && setConservativeValue(null);
                    }}
                    onInputKeyDown={onEnterKeyDown}
                    onSearch={(value) => setSearchTerm(value)}
                    optionLabelProp="label"
                    options={realOptions}
                    filterOption={false}
                    value={transformFormValue(conservativeValue ?? value)}
                    {...(isNewValueAllowed ? { mode: 'tags', popupClassName: 'tickets-tags' } : { mode: 'multiple' })}
                    onDropdownVisibleChange={setIsSuggestionRequested}
                    dropdownRender={(option) => (
                        <>
                            {!debouncedSearchTerm && !searchResult?.length && !!realOptions?.length && (
                                <Row justify="center">
                                    <Typography.Text style={{ marginBottom: marginXXS }}>
                                        {t('autocomplete.empty')}
                                    </Typography.Text>
                                    <CustomDivider zeroMargin />
                                </Row>
                            )}
                            {debouncedSearchTerm && !searchResult?.length && !isLoading && !error && (
                                <Row justify="center">
                                    <Typography.Text style={{ marginBottom: marginXXS }}>
                                        {t('autocomplete.noResults')}
                                    </Typography.Text>
                                    <CustomDivider zeroMargin />
                                </Row>
                            )}
                            {error && !!realOptions?.length && (
                                <Row justify="center">
                                    <Typography.Text style={{ marginBottom: marginXXS, color: colorError }}>
                                        {error.message}
                                    </Typography.Text>
                                    <CustomDivider zeroMargin />
                                </Row>
                            )}
                            <>{option}</>
                        </>
                    )}
                    {...rest}
                />
            </GrispiInputContainer>
        );
    }
    return (
        <GrispiInputContainer {...containerProps}>
            <Skeleton.Input active className="full-width" />
        </GrispiInputContainer>
    );
};

export default GrispiMultiUserInput;
